import React, { useEffect, useRef } from 'react';
import { usePDFLoader } from '../../lib/pdf/hooks/usePDFLoader';
import { renderPDFPage } from '../../lib/pdf/renderer';

interface PDFViewerProps {
  file: File;
  onError?: (error: string) => void;
  className?: string;
}

export default function PDFViewer({
  file,
  onError,
  className = ''
}: PDFViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { pdfDoc, loading, error } = usePDFLoader(file);

  useEffect(() => {
    if (error && onError) {
      onError(error);
    }
  }, [error, onError]);

  useEffect(() => {
    let mounted = true;

    const renderPage = async () => {
      if (!pdfDoc || !canvasRef.current || !mounted) return;

      try {
        await renderPDFPage(canvasRef.current, pdfDoc, 1, 1);
      } catch (err) {
        console.error('PDF render error:', err);
        if (onError && mounted) {
          onError('Failed to render PDF page');
        }
      }
    };

    renderPage();

    return () => {
      mounted = false;
    };
  }, [pdfDoc, onError]);

  if (loading) {
    return (
      <div className="w-full h-64 flex items-center justify-center bg-black/20 rounded-lg">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" />
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className={`relative max-w-4xl mx-auto bg-black/20 rounded-lg overflow-hidden ${className}`}
    >
      <canvas 
        ref={canvasRef}
        className="w-full h-auto"
        style={{ 
          imageRendering: 'high-quality',
          maxWidth: '100%'
        }}
      />
    </div>
  );
}